import { useEffect, useMemo, useRef, useState } from "react";
import { Scissors } from "lucide-react";

/**
 * RevenueChart — Revenue / services / both, smooth bezier area chart.
 *
 * Props:
 *   data           : [{ hour: 9-21, revenue: number, services_count: number }]
 *   peakHour       : int | null
 *   peakAmount     : number
 *   avgPerHour     : number   (₹/hr across non-zero hours)
 *   paymentBreakdown : { cash, upi, card } totals — used for the tooltip mini-bars
 *   dateLabel      : "Mon, 28 May"
 */
export function RevenueChart({
  data = [], peakHour = null, peakAmount = 0, avgPerHour = 0,
  paymentBreakdown = null, dateLabel = "",
}) {
  const [mode, setMode] = useState("revenue");   // revenue | services | both
  const [hoverIdx, setHoverIdx] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const pathRef = useRef(null);
  const containerRef = useRef(null);

  // Width listener
  const [W, setW] = useState(900);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(320, Math.floor(e.contentRect.width)));
    });
    ro.observe(el);
    setW(Math.max(320, Math.floor(el.getBoundingClientRect().width || 900)));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const m = () => setIsMobile(window.innerWidth < 768);
    m();
    window.addEventListener("resize", m);
    return () => window.removeEventListener("resize", m);
  }, []);

  // Click outside to dismiss on mobile
  useEffect(() => {
    if (!isMobile || hoverIdx === null) return;
    const dismiss = (e) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target)) setHoverIdx(null);
    };
    window.addEventListener("click", dismiss);
    return () => window.removeEventListener("click", dismiss);
  }, [isMobile, hoverIdx]);

  // Dataset based on mode
  const dataset = useMemo(() => {
    if (mode === "services") return data.map((d) => ({ ...d, y: d.services_count || 0 }));
    return data.map((d) => ({ ...d, y: d.revenue || 0 }));
  }, [data, mode]);

  const yLabel = mode === "services" ? "services" : "revenue";
  const maxY = Math.max(1, ...dataset.map((d) => d.y));

  // Layout
  const H = isMobile ? 180 : 260;
  const padTop = 48, padBottom = 40, padLeft = 60, padRight = 24;
  const innerW = Math.max(1, W - padLeft - padRight);
  const innerH = Math.max(1, H - padTop - padBottom);
  const N = Math.max(dataset.length, 1);
  const xAt = (i) => padLeft + (i / (N - 1)) * innerW;
  const yAt = (v) => padTop + innerH - (v / maxY) * innerH;

  // Smooth cubic-bezier path
  const buildSmoothPath = (pts, closed = false) => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const tension = 0.18;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    if (closed) {
      const last = pts[pts.length - 1];
      const first = pts[0];
      d += ` L ${last.x} ${padTop + innerH} L ${first.x} ${padTop + innerH} Z`;
    }
    return d;
  };

  const points = dataset.map((d, i) => ({ x: xAt(i), y: yAt(d.y), v: d.y, hour: d.hour, count: d.services_count }));
  const linePath = buildSmoothPath(points, false);
  const areaPath = buildSmoothPath(points, true);

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    p, value: Math.round(maxY * p),
    y: padTop + innerH - p * innerH,
  }));

  // Current hour
  const nowHour = new Date().getHours();

  // Stroke draw-on animation — only on mount + mode change, NOT on resize
  const lastModeRef = useRef(mode);
  useEffect(() => {
    const p = pathRef.current;
    if (!p) return;
    const modeChanged = lastModeRef.current !== mode;
    lastModeRef.current = mode;
    if (loaded && !modeChanged) return;   // skip on resize
    let len;
    try { len = p.getTotalLength(); } catch { len = 0; }
    if (!len) { setLoaded(true); return; }
    p.style.strokeDasharray = `${len}`;
    p.style.strokeDashoffset = `${len}`;
    requestAnimationFrame(() => {
      p.style.transition = "stroke-dashoffset 1200ms ease-out";
      p.style.strokeDashoffset = "0";
      setLoaded(true);
    });
  }, [mode, linePath]); // eslint-disable-line

  // Click-to-toggle on mobile, hover on desktop
  const handlePointerOver = (i) => setHoverIdx(i);
  const handlePointerLeave = () => setHoverIdx(null);

  const total = data.reduce((s, d) => s + (d.revenue || 0), 0);
  const empty = total === 0;

  // ============================================================
  // Empty state
  // ============================================================
  if (empty) {
    return (
      <div className="glass-primary rounded-[20px] p-6" data-testid="revenue-chart">
        <ChartHeader dateLabel={dateLabel} peakHour={peakHour} peakAmount={peakAmount} total={0} avgPerHour={0} mode={mode} setMode={setMode} />
        <div className="rounded-2xl flex flex-col items-center justify-center gap-3" style={{ height: isMobile ? 180 : 260, border: "2px dashed rgba(212,168,83,0.30)" }}>
          <Scissors size={28} className="text-[var(--color-gold-mid)]" />
          <div className="font-inter text-[13.5px] text-[var(--color-text-muted)] text-center px-6">
            Revenue will appear as services are completed today
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-primary rounded-[20px] p-6" data-testid="revenue-chart">
      <ChartHeader
        dateLabel={dateLabel} peakHour={peakHour} peakAmount={peakAmount}
        total={total} avgPerHour={avgPerHour} mode={mode} setMode={setMode}
      />

      <div ref={containerRef} className="relative" style={{ height: H }}>
        <svg width={W} height={H} className="block">
          <defs>
            <linearGradient id="rc-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(212,168,83,0.35)" />
              <stop offset="100%" stopColor="rgba(212,168,83,0.02)" />
            </linearGradient>
          </defs>

          {/* Grid + Y labels */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={padLeft} y1={t.y} x2={W - padRight} y2={t.y}
                    stroke="rgba(212,168,83,0.14)" strokeWidth={1} />
              <text x={padLeft - 10} y={t.y + 4} textAnchor="end"
                    style={{ fontFamily: "DM Mono, monospace", fontSize: 11, fill: "#B5892E" }}>
                {mode === "services" ? t.value : `₹${t.value.toLocaleString("en-IN")}`}
              </text>
            </g>
          ))}

          {/* Peak callout (vertical dashed) */}
          {peakHour != null && (() => {
            const idx = data.findIndex((d) => d.hour === peakHour);
            if (idx === -1) return null;
            const px = xAt(idx);
            const py = yAt(mode === "services" ? data[idx].services_count : data[idx].revenue);
            return (
              <g style={{ opacity: loaded ? 1 : 0, transition: "opacity 600ms ease 800ms" }}>
                <line x1={px} y1={py + 8} x2={px} y2={padTop + innerH}
                      stroke="#D4A853" strokeOpacity={0.45} strokeDasharray="4 4" strokeWidth={1.2} />
              </g>
            );
          })()}

          {/* Area fill */}
          <path d={areaPath} fill="url(#rc-area)"
                style={{ opacity: loaded ? 1 : 0, transition: "opacity 800ms ease 400ms" }} />

          {/* Stroke line */}
          <path ref={pathRef} d={linePath} fill="none" stroke="#D4A853" strokeWidth={2.5}
                strokeLinecap="round" strokeLinejoin="round" />

          {/* Data points + invisible hit targets */}
          {points.map((p, i) => {
            const isPeak = data[i].hour === peakHour && (mode === "revenue" ? data[i].revenue > 0 : data[i].services_count > 0);
            const isNow = data[i].hour === nowHour;
            const isZero = (mode === "services" ? data[i].services_count : data[i].revenue) === 0;
            const isHover = hoverIdx === i;
            const r = isNow ? 6 : isZero ? 3 : 4;
            const fill = isNow ? "#D4A853" : "#FAF0F0";
            const stroke = isNow ? "#8E6A1A" : "#D4A853";
            return (
              <g key={i}>
                {/* Invisible hit zone */}
                <rect
                  x={xAt(i) - (innerW / (N - 1)) / 2}
                  y={padTop}
                  width={innerW / (N - 1)}
                  height={innerH}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => handlePointerOver(i)}
                  onMouseLeave={handlePointerLeave}
                  onClick={() => setHoverIdx((cur) => cur === i ? null : i)}
                  data-testid={`revenue-chart-point-${data[i].hour}`}
                />
                {/* Mode=both: circle markers sized by service count */}
                {mode === "both" && data[i].services_count > 0 && (
                  <circle cx={p.x} cy={p.y} r={Math.min(12, 3 + data[i].services_count * 2)}
                          fill="rgba(94,64,96,0.18)" stroke="#5E4060" strokeWidth={1.5}
                          style={{ transition: "all 400ms" }} />
                )}
                <circle
                  cx={p.x} cy={p.y}
                  r={isHover ? r + 2 : r}
                  fill={fill} stroke={stroke} strokeWidth={isPeak || isNow ? 2.5 : 2}
                  opacity={isZero ? 0.35 : 1}
                  style={{
                    transition: "r 200ms ease",
                    animation: isNow ? "rcPulse 2s ease-in-out infinite" : "none",
                  }}
                />
                {/* Peak pill */}
                {isPeak && (
                  <g style={{ opacity: loaded ? 1 : 0, transition: "opacity 600ms ease 900ms" }}>
                    <rect
                      x={p.x - 38} y={p.y - 32}
                      width={76} height={20} rx={10}
                      fill="#D4A853"
                    />
                    <text x={p.x} y={p.y - 18} textAnchor="middle"
                          style={{ fontFamily: "DM Mono, monospace", fontSize: 11, fontWeight: 600, fill: "#fff" }}>
                      PEAK ₹{(mode === "services" ? data[i].services_count : data[i].revenue).toLocaleString("en-IN")}
                    </text>
                    <polygon points={`${p.x - 4},${p.y - 12} ${p.x + 4},${p.y - 12} ${p.x},${p.y - 8}`} fill="#D4A853" />
                  </g>
                )}
              </g>
            );
          })}

          {/* X-axis labels */}
          {data.map((d, i) => {
            const showOnMobile = [9, 12, 15, 18, 21].includes(d.hour);
            if (isMobile && !showOnMobile) return null;
            const isNow = d.hour === nowHour;
            const showMeridiem = [9, 12, 21].includes(d.hour);
            return (
              <g key={d.hour}>
                <text
                  x={xAt(i)} y={padTop + innerH + 18}
                  textAnchor="middle"
                  style={{ fontFamily: "DM Mono, monospace", fontSize: 11,
                           fill: isNow ? "#D4A853" : "#A8A29E", fontWeight: isNow ? 600 : 400 }}
                >
                  {d.hour > 12 ? d.hour - 12 : d.hour}
                </text>
                {showMeridiem && (
                  <text x={xAt(i)} y={padTop + innerH + 30}
                        textAnchor="middle"
                        style={{ fontFamily: "Inter, sans-serif", fontSize: 9, fill: "#A8A29E" }}>
                    {d.hour < 12 ? "AM" : "PM"}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoverIdx !== null && data[hoverIdx] && (
          <Tooltip
            data={data[hoverIdx]}
            x={xAt(hoverIdx)}
            y={yAt(mode === "services" ? data[hoverIdx].services_count : data[hoverIdx].revenue)}
            isMobile={isMobile}
            paymentBreakdown={paymentBreakdown}
          />
        )}
      </div>
    </div>
  );
}

// ====================================================
// Header (title + chips + mode toggle)
// ====================================================
function ChartHeader({ dateLabel, peakHour, peakAmount, total, avgPerHour, mode, setMode }) {
  const peakLabel = peakHour
    ? `${peakHour > 12 ? peakHour - 12 : peakHour}:00 ${peakHour < 12 ? "AM" : "PM"}`
    : "—";
  return (
    <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
      <div>
        <div className="overline">Revenue by hour</div>
        <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-0.5">
          9 AM → 9 PM{dateLabel ? ` · ${dateLabel}` : ""}
        </div>
        <div className="flex gap-1.5 mt-3" data-testid="revenue-chart-mode-toggle">
          {[
            { k: "revenue", label: "Revenue" },
            { k: "services", label: "Services" },
            { k: "both", label: "Both" },
          ].map((m) => (
            <button
              key={m.k}
              onClick={() => setMode(m.k)}
              data-testid={`revenue-chart-mode-${m.k}`}
              className={`px-3 py-1 rounded-full text-[11.5px] font-inter font-medium border transition ${mode === m.k ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        <Chip bg="rgba(212,168,83,0.14)" fg="#8E6A1A" label="Peak" value={peakLabel} testid="revenue-chart-chip-peak" />
        <Chip bg="rgba(94,64,96,0.12)"  fg="#5E4060" label="Total" value={`₹${Math.round(total).toLocaleString("en-IN")}`} testid="revenue-chart-chip-total" />
        <Chip bg="rgba(255,255,255,0.55)" fg="#3F2A45" label="Avg/hr" value={`₹${Math.round(avgPerHour).toLocaleString("en-IN")}`} testid="revenue-chart-chip-avg" />
      </div>
    </div>
  );
}

function Chip({ bg, fg, label, value, testid }) {
  return (
    <div className="rounded-full px-3 py-1.5 flex items-center gap-1.5" style={{ background: bg, color: fg }} data-testid={testid}>
      <span className="font-inter text-[10.5px] uppercase tracking-[0.18em] opacity-75">{label}</span>
      <span className="font-mono text-[12px]">{value}</span>
    </div>
  );
}

// ====================================================
// Tooltip
// ====================================================
function Tooltip({ data, x, y, isMobile, paymentBreakdown }) {
  const fmtHour = (h) => `${h > 12 ? h - 12 : h}:00 ${h < 12 ? "AM" : "PM"}`;
  const breakdown = paymentBreakdown || { cash: 0, upi: 0, card: 0 };
  const total = (breakdown.cash || 0) + (breakdown.upi || 0) + (breakdown.card || 0);
  const pct = (v) => total > 0 ? Math.max(2, Math.round((v / total) * 100)) : 0;

  const card = (
    <div className="font-inter text-[13px]">
      <div className="font-sora font-semibold text-[var(--color-text-primary)]">{fmtHour(data.hour)}</div>
      <div className="font-mono text-[22px] text-[var(--color-gold-deeper)] mt-0.5 leading-tight">
        ₹{Math.round(data.revenue || 0).toLocaleString("en-IN")}
      </div>
      {total > 0 && (data.revenue > 0) && (
        <div className="flex gap-0.5 h-1.5 rounded-full overflow-hidden mt-2 w-full" style={{ background: "rgba(28,25,23,0.06)" }}>
          <div title={`Cash ₹${breakdown.cash}`} style={{ width: `${pct(breakdown.cash)}%`, background: "#16A34A" }} />
          <div title={`UPI ₹${breakdown.upi}`}  style={{ width: `${pct(breakdown.upi)}%`,  background: "#7C5C7A" }} />
          <div title={`Card ₹${breakdown.card}`} style={{ width: `${pct(breakdown.card)}%`, background: "#D4A853" }} />
        </div>
      )}
      <div className="font-inter text-[11px] text-[var(--color-text-muted)] mt-1.5">
        {data.services_count || 0} service{data.services_count === 1 ? "" : "s"} completed
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div
        className="fixed left-0 right-0 bottom-0 m-3 p-3 rounded-2xl z-30 anim-slide-up"
        style={{
          background: "rgba(255,255,255,0.94)", border: "1px solid rgba(212,168,83,0.40)",
          backdropFilter: "blur(20px)", boxShadow: "0 8px 24px rgba(28,25,23,0.18)",
        }}
        data-testid="revenue-chart-tooltip"
      >
        {card}
      </div>
    );
  }
  return (
    <div
      className="absolute pointer-events-none z-20"
      style={{
        left: x, top: y - 24, transform: "translate(-50%, -100%)",
        background: "rgba(255,255,255,0.92)", border: "1px solid rgba(212,168,83,0.4)",
        borderRadius: 10, padding: "10px 14px",
        backdropFilter: "blur(20px)", boxShadow: "0 8px 24px rgba(28,25,23,0.12)",
        minWidth: 160,
      }}
      data-testid="revenue-chart-tooltip"
    >
      {card}
    </div>
  );
}

export default RevenueChart;
